/**
 * Phone Detail Page
 *
 * Displays comprehensive information for a single phone number.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cn, formatDate, formatDateTime } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { supabase } from '@/lib/supabase';
import { AppLayout, PageHeader } from '@/components/layout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button, IconButton } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge, FlagStatusBadge, ReputationSourceBadge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import {
  usePhoneStore,
  useSelectedPhone,
  useSelectedPhoneActivities,
  useCallGroups,
} from '@/stores/phoneStore';
import { useIsAdmin, useCanEdit } from '@/stores/authStore';
import { ROUTES, LINE_TYPE_LABELS, OPERATING_COMPANY_TYPE_LABELS, US_STATES, REPUTATION_SOURCE_COLORS } from '@/constants';
import type { PhoneActivity, PhoneCheckResult, DeviceCloudResult } from '@/types';
import {
  ArrowLeft,
  Save,
  X,
  Edit2,
  Archive,
  RefreshCw,
  Clock,
  MapPin,
  Building2,
  Phone as PhoneIcon,
  Tag,
  FileText,
  User,
  Hash,
  AlertTriangle,
  CheckCircle2,
  Info,
  Activity,
  Shield,
  Smartphone,
} from 'lucide-react';

// ============================================================================
// Component
// ============================================================================

export function PhoneDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();

  const phone = useSelectedPhone();
  const activities = useSelectedPhoneActivities();
  const callGroups = useCallGroups();
  const isAdmin = useIsAdmin();
  const canEdit = useCanEdit();

  const {
    fetchPhoneDetails,
    updatePhone,
    clearSelectedPhone,
    isLoadingDetails,
    isSaving,
  } = usePhoneStore();

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    description: '',
    phoneNumberGroup: '',
    internalId: '',
  });
  const [activeTab, setActiveTab] = useState<'activity' | 'checks' | 'devices'>('activity');

  // Fetch phone details on mount
  useEffect(() => {
    if (id) {
      Logger.info(LogCategory.UI, 'PhoneDetailPage mounted', 'PhoneDetailPage', { phoneId: id });
      fetchPhoneDetails(id);
    }

    return () => {
      clearSelectedPhone();
    };
  }, [id, fetchPhoneDetails, clearSelectedPhone]);

  // Initialize edit data when phone loads
  useEffect(() => {
    if (phone) {
      setEditData({
        description: phone.description || '',
        phoneNumberGroup: phone.callGroup || '',
        internalId: phone.internalId || '',
      });
    }
  }, [phone]);

  const handleBack = () => {
    Logger.info(LogCategory.NAVIGATION, 'Navigating back to phone list', 'PhoneDetailPage');
    navigate(ROUTES.PHONE_NUMBERS);
  };

  const handleStartEdit = () => {
    Logger.info(LogCategory.UI, 'Starting edit mode', 'PhoneDetailPage');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    Logger.info(LogCategory.UI, 'Canceling edit', 'PhoneDetailPage');
    setIsEditing(false);
    if (phone) {
      setEditData({
        description: phone.description || '',
        phoneNumberGroup: phone.callGroup || '',
        internalId: phone.internalId || '',
      });
    }
  };

  const handleSave = async () => {
    if (!phone) return;

    Logger.info(LogCategory.DATA, 'Saving phone updates', 'PhoneDetailPage', {
      phoneId: phone.id,
      updates: editData,
    });

    try {
      await updatePhone(phone.id, {
        description: editData.description || null,
        callGroup: editData.phoneNumberGroup || null,
        internalId: editData.internalId || null,
      });
      setIsEditing(false);
      showSuccess('Changes saved', 'Phone number details have been updated.');
    } catch (error) {
      Logger.error(LogCategory.DATA, 'Failed to save phone updates', 'PhoneDetailPage', undefined, error);
      showError('Save failed', 'Failed to update phone number. Please try again.');
    }
  };

  const handleArchive = async () => {
    if (!phone) return;

    Logger.info(LogCategory.DATA, 'Archiving phone', 'PhoneDetailPage', { phoneId: phone.id });

    try {
      await updatePhone(phone.id, { isArchived: true });
      showSuccess('Phone archived', 'The phone number has been archived.');
      navigate(ROUTES.PHONE_NUMBERS);
    } catch (error) {
      Logger.error(LogCategory.DATA, 'Failed to archive phone', 'PhoneDetailPage', undefined, error);
      showError('Archive failed', 'Failed to archive phone number. Please try again.');
    }
  };

  // Loading state
  if (isLoadingDetails) {
    return (
      <AppLayout>
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-48 mb-4" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="h-6 bg-slate-200 rounded w-32 mb-4" />
                <div className="space-y-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i}>
                      <div className="h-4 bg-slate-200 rounded w-20 mb-2" />
                      <div className="h-5 bg-slate-200 rounded w-full" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl p-6 border border-slate-200 h-96" />
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Not found state
  if (!phone) {
    return (
      <AppLayout>
        <div className="text-center py-16">
          <PhoneIcon className="mx-auto h-16 w-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Phone Not Found</h2>
          <p className="mt-2 text-slate-600">The phone number you're looking for doesn't exist.</p>
          <Button onClick={handleBack} className="mt-6">
            Back to Phone Numbers
          </Button>
        </div>
      </AppLayout>
    );
  }

  // Build phone number group options
  const phoneNumberGroupOptions = [
    { value: '', label: 'No Group' },
    ...callGroups.map((group) => ({
      value: group.name,
      label: group.name,
    })),
  ];

  return (
    <AppLayout>
      <PageHeader
        title={phone.formattedNumber}
        description="Phone number details"
        breadcrumbs={[
          { label: 'Phone Numbers', path: ROUTES.PHONE_NUMBERS },
          { label: phone.formattedNumber },
        ]}
        actions={
          <>
            {canEdit && !isEditing && (
              <Button
                variant="outline"
                leftIcon={<Edit2 className="h-4 w-4" />}
                onClick={handleStartEdit}
              >
                Edit
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                leftIcon={<Archive className="h-4 w-4" />}
                onClick={handleArchive}
              >
                Archive
              </Button>
            )}
            <Button
              variant="outline"
              leftIcon={<RefreshCw className="h-4 w-4" />}
            >
              Check Now
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Phone Information */}
        <div className="lg:col-span-1 space-y-6">
          {/* Status Card */}
          <Card>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <FlagStatusBadge status={phone.flagStatus} size="lg" />
                <div className="text-right">
                  <p className="text-sm text-slate-500">Reputation Score</p>
                  <p className={cn(
                    'text-2xl font-bold',
                    phone.reputationScore >= 80 ? 'text-success-600' :
                    phone.reputationScore >= 50 ? 'text-warning-600' :
                    'text-danger-600'
                  )}>
                    {phone.reputationScore}
                  </p>
                </div>
              </div>

              {phone.reputationSources.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2">Flagged By</p>
                  <div className="flex flex-wrap gap-2">
                    {phone.reputationSources.map((source) => (
                      <ReputationSourceBadge key={source} source={source} />
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Phone Details Card */}
          <Card>
            <CardHeader
              title="Phone Information"
              action={
                isEditing && (
                  <div className="flex items-center gap-2">
                    <IconButton
                      icon={<X className="h-4 w-4" />}
                      variant="ghost"
                      size="sm"
                      aria-label="Cancel"
                      onClick={handleCancelEdit}
                    />
                    <IconButton
                      icon={<Save className="h-4 w-4" />}
                      variant="primary"
                      size="sm"
                      aria-label="Save"
                      onClick={handleSave}
                      disabled={isSaving}
                    />
                  </div>
                )
              }
            />
            <CardContent>
              <div className="space-y-4">
                {/* Phone Number */}
                <div className="flex items-start gap-3">
                  <PhoneIcon className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-500">Phone Number</p>
                    <p className="font-mono text-slate-900">{phone.formattedNumber}</p>
                  </div>
                </div>

                {/* Phone Number Group */}
                <div className="flex items-start gap-3">
                  <Tag className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-500">Group</p>
                    {isEditing ? (
                      <Select
                        value={editData.phoneNumberGroup}
                        onChange={(e) => setEditData({ ...editData, phoneNumberGroup: e.target.value })}
                        options={phoneNumberGroupOptions}
                        size="sm"
                      />
                    ) : (
                      <p className="text-slate-900">{phone.callGroup || '—'}</p>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-500">Description</p>
                    {isEditing ? (
                      <Input
                        value={editData.description}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        placeholder="Add description..."
                        size="sm"
                      />
                    ) : (
                      <p className="text-slate-900">{phone.description || '—'}</p>
                    )}
                  </div>
                </div>

                {/* Internal ID */}
                <div className="flex items-start gap-3">
                  <Hash className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-500">Internal ID</p>
                    {isEditing ? (
                      <Input
                        value={editData.internalId}
                        onChange={(e) => setEditData({ ...editData, internalId: e.target.value })}
                        placeholder="Add internal ID..."
                        size="sm"
                      />
                    ) : (
                      <p className="text-slate-900 font-mono">{phone.internalId || '—'}</p>
                    )}
                  </div>
                </div>

                {/* Last Checked */}
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-500">Last Checked</p>
                    <p className="text-slate-900">{formatDateTime(phone.lastCheckedDate)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Activity & Tabs */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="border-b border-slate-200">
              <nav className="flex -mb-px">
                {[
                  { id: 'activity', label: 'Activity', icon: Activity },
                  { id: 'checks', label: 'Reputation Checks', icon: Shield },
                  { id: 'devices', label: 'Device Cloud', icon: Smartphone },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      Logger.debug(LogCategory.UI, 'Tab changed', 'PhoneDetailPage', { tab: tab.id });
                      setActiveTab(tab.id as typeof activeTab);
                    }}
                    className={cn(
                      'flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    )}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {/* Activity Tab */}
              {activeTab === 'activity' && (
                <div className="space-y-4">
                  {activities.length === 0 ? (
                    <div className="text-center py-8">
                      <Activity className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                      <p className="text-slate-600">No activity recorded yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {activities.map((activity) => (
                        <ActivityItem key={activity.id} activity={activity} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Checks Tab */}
              {activeTab === 'checks' && (
                <CheckResultsTab phoneId={phone.id} companyId={phone.companyId} reputationSources={phone.reputationSources} />
              )}

              {/* Devices Tab */}
              {activeTab === 'devices' && (
                <div className="text-center py-8">
                  <Smartphone className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                  <p className="font-medium text-slate-900">Device Cloud Data</p>
                  <p className="text-sm text-slate-600 mt-1">
                    Device cloud integration coming soon.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

// ============================================================================
// Activity Item Component
// ============================================================================

function ActivityItem({ activity }: { activity: PhoneActivity }) {
  const getIcon = () => {
    switch (activity.type) {
      case 'flagged':
        return <AlertTriangle className="h-5 w-5 text-danger-500" />;
      case 'unflagged':
        return <CheckCircle2 className="h-5 w-5 text-success-500" />;
      case 'checked':
        return <RefreshCw className="h-5 w-5 text-primary-500" />;
      case 'edited':
        return <Edit2 className="h-5 w-5 text-slate-500" />;
      case 'archived':
        return <Archive className="h-5 w-5 text-slate-500" />;
      default:
        return <Info className="h-5 w-5 text-slate-500" />;
    }
  };

  const getLabel = () => {
    switch (activity.type) {
      case 'flagged':
        return 'Flagged';
      case 'unflagged':
        return 'Cleared';
      case 'checked':
        return 'Checked';
      case 'edited':
        return 'Edited';
      case 'archived':
        return 'Archived';
      case 'restored':
        return 'Restored';
      default:
        return activity.type;
    }
  };

  return (
    <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
      <div className="shrink-0 mt-0.5">{getIcon()}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900">{getLabel()}</span>
          {activity.source !== 'system' && activity.source !== 'user' && (
            <ReputationSourceBadge source={activity.source} size="sm" />
          )}
        </div>
        {activity.details && (
          <p className="text-sm text-slate-600 mt-1">{activity.details}</p>
        )}
        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
          <span>{formatDateTime(activity.date)}</span>
          {activity.userName && (
            <>
              <span>•</span>
              <span>{activity.userName}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Check Results Tab
// ============================================================================

function CheckResultsTab({ phoneId }: { phoneId: string; companyId: string; reputationSources: string[] }) {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadResults() {
      const { data } = await (supabase.from('phone_check_results') as any)
        .select('*')
        .eq('phone_id', phoneId)
        .order('checked_at', { ascending: false })
        .limit(20);
      setResults(data || []);
      setLoading(false);
    }
    loadResults();
  }, [phoneId]);

  const handleViewScreenshot = async (path: string) => {
    const { data } = await supabase.storage
      .from('pnt-screenshots')
      .createSignedUrl(path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  if (loading) return <div className="py-8 text-center text-slate-500">Loading check results...</div>;

  if (results.length === 0) {
    return (
      <div className="text-center py-8">
        <CheckCircle2 className="mx-auto h-12 w-12 text-success-300 mb-4" />
        <p className="font-medium text-slate-900">No Check Results Yet</p>
        <p className="text-sm text-slate-600">Results will appear here after reputation checks run.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500 mb-4">
        Showing {results.length} most recent check results.
      </p>
      {results.map((result: any) => {
        const isFlagged = result.status === 'flagged';
        const screenshotPath = result.raw_response?.screenshot_path;
        return (
          <div key={result.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={cn('h-3 w-3 rounded-full', isFlagged ? 'bg-red-500' : 'bg-green-500')} />
              <div>
                <span className="font-medium text-slate-900 text-sm">{String(result.source)}</span>
                <span className={cn('ml-2 text-xs px-2 py-0.5 rounded-full', isFlagged ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700')}>
                  {isFlagged ? 'Flagged' : 'Clean'}
                </span>
                {result.raw_response?.spam_label_text && (
                  <span className="ml-2 text-xs text-slate-500">"{String(result.raw_response.spam_label_text)}"</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span>{formatDate(result.checked_at, 'short')}</span>
              {result.confidence > 0 && <span>{Math.round(Number(result.confidence) * 100)}%</span>}
              {screenshotPath && (
                <button
                  onClick={() => handleViewScreenshot(screenshotPath)}
                  className="text-primary-600 hover:text-primary-800 font-medium"
                >
                  View Screenshot
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
